import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { UserService } from './userService';
import type { Database } from '../types/database.types';
import { supabase } from './supabase';
import { logger } from '../utils/logger';

type User = Database['public']['Tables']['users']['Row'];
type Company = Database['public']['Tables']['companies']['Row'];
type Branch = Database['public']['Tables']['branches']['Row'];

export interface ExcelUserData {
  아이디: string;
  이름: string;
  전화번호?: string;
  회사명?: string;
  지점명?: string;
  관리자권한?: '일반' | '관리자' | '슈퍼관리자';
}

export interface UploadResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    row: number;
    message: string;
    data?: ExcelUserData;
  }>;
}

export class ExcelService {
  // 엑셀 템플릿 다운로드
  static downloadTemplate() {
    const templateData: ExcelUserData[] = [
      {
        아이디: '01012345678',
        이름: '홍길동',
        전화번호: '010-1234-5678',
        회사명: '',
        지점명: '',
        관리자권한: '일반',
      },
      {
        아이디: '01098765432',
        이름: '김철수',
        전화번호: '010-9876-5432',
        회사명: '',
        지점명: '',
        관리자권한: '관리자',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '사용자목록');

    // 컬럼 너비 설정
    ws['!cols'] = [
      { wch: 15 }, // 아이디
      { wch: 12 }, // 이름
      { wch: 15 }, // 전화번호
      { wch: 20 }, // 회사명
      { wch: 15 }, // 지점명
      { wch: 12 }, // 관리자권한
    ];

    // 파일 다운로드
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, '사용자등록_템플릿.xlsx');
  }

  // 사용자 목록 엑셀 다운로드
  static async downloadUserList(filters?: any) {
    try {
      // 사용자 목록 조회
      const users = await UserService.getUsers(filters);
      
      // 엑셀 데이터 형식으로 변환
      const excelData: ExcelUserData[] = users.map(user => ({
        아이디: user.username,
        이름: user.name,
        전화번호: user.phone_number || '',
        회사명: user.company?.name || '',
        지점명: user.branch?.branch_name || '',
        관리자권한: user.is_admin ? '슈퍼관리자' : 
                  user.has_admin_role ? '관리자' : '일반',
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '사용자목록');

      // 컬럼 너비 설정
      ws['!cols'] = [
        { wch: 15 }, // 아이디
        { wch: 12 }, // 이름
        { wch: 15 }, // 전화번호
        { wch: 20 }, // 회사명
        { wch: 15 }, // 지점명
        { wch: 12 }, // 관리자권한
      ];

      // 파일 다운로드
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const today = new Date().toISOString().split('T')[0];
      saveAs(blob, `사용자목록_${today}.xlsx`);
    } catch (error) {
      logger.error('Excel download error', error);
      throw error;
    }
  }

  // 엑셀 파일 업로드 및 처리
  static async uploadUserList(file: File): Promise<UploadResult> {
    logger.debug('uploadUserList started', { fileName: file.name, fileSize: file.size });
    const result: UploadResult = {
      success: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      errors: [],
    };

    try {
      // 파일 읽기
      logger.debug('Reading file...');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      logger.debug('Sheet name', { sheetName });
      const worksheet = workbook.Sheets[sheetName];
      
      // JSON으로 변환
      const jsonData: ExcelUserData[] = XLSX.utils.sheet_to_json(worksheet);
      logger.debug('Parsed data', { recordCount: jsonData.length });
      result.totalRows = jsonData.length;

      // 회사 및 지점 목록 조회
      const companies = await UserService.getCompanies();
      const branches = await UserService.getBranches();

      // 각 행 처리
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        const rowNum = i + 2; // 헤더 제외

        try {
          // 유효성 검사
          if (!row.아이디) {
            throw new Error('아이디는 필수 항목입니다');
          }
          if (!row.이름) {
            throw new Error('이름은 필수 항목입니다');
          }

          // 중복 확인
          const { data: existingUser } = await supabase
            .from('users')
            .select('id')
            .eq('username', row.아이디)
            .single();

          if (existingUser) {
            throw new Error('이미 존재하는 아이디입니다');
          }

          // 회사 찾기
          let companyId: string | undefined;
          if (row.회사명 && row.회사명.trim() !== '') {
            const company = companies.find(c => c.name === row.회사명);
            if (!company) {
              throw new Error(`존재하지 않는 회사명입니다: ${row.회사명}`);
            }
            companyId = company.id;
          }

          // 지점 찾기
          let branchId: string | undefined;
          if (row.지점명 && row.지점명.trim() !== '' && companyId) {
            const branch = branches.find(
              b => b.branch_name === row.지점명 && b.company_id === companyId
            );
            if (!branch) {
              throw new Error(`존재하지 않는 지점명입니다: ${row.지점명}`);
            }
            branchId = branch.id;
          }

          // 권한 설정
          const isAdmin = row.관리자권한 === '슈퍼관리자';
          const hasAdminRole = row.관리자권한 === '관리자' || isAdmin;

          // 사용자 생성
          await UserService.createUser({
            username: row.아이디,
            name: row.이름,
            password: row.아이디, // 초기 비밀번호는 아이디와 동일
            companyId,
            branchId,
            phoneNumber: row.전화번호?.replace(/-/g, ''),
            isAdmin,
            hasAdminRole,
          });

          result.successCount++;
        } catch (error: any) {
          result.errorCount++;
          result.errors.push({
            row: rowNum,
            message: error.message || '알 수 없는 오류',
            data: row,
          });
        }
      }

      result.success = result.errorCount === 0;
      return result;
    } catch (error) {
      logger.error('Excel upload error', error);
      throw error;
    }
  }

  // 업로드 오류 내역 다운로드
  static downloadErrorReport(errors: UploadResult['errors']) {
    const errorData = errors.map(error => ({
      행번호: error.row,
      오류내용: error.message,
      아이디: error.data?.아이디 || '',
      이름: error.data?.이름 || '',
      전화번호: error.data?.전화번호 || '',
      회사명: error.data?.회사명 || '',
      지점명: error.data?.지점명 || '',
      관리자권한: error.data?.관리자권한 || '',
    }));

    const ws = XLSX.utils.json_to_sheet(errorData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '오류내역');

    // 컬럼 너비 설정
    ws['!cols'] = [
      { wch: 10 }, // 행번호
      { wch: 40 }, // 오류내용
      { wch: 15 }, // 아이디
      { wch: 12 }, // 이름
      { wch: 15 }, // 전화번호
      { wch: 20 }, // 회사명
      { wch: 15 }, // 지점명
      { wch: 12 }, // 관리자권한
    ];

    // 파일 다운로드
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, '업로드_오류내역.xlsx');
  }
}